import type { AxiosInstance, AxiosResponseTransformer, ResponseType } from 'axios';
import axios from 'axios';
import { camelizeKeys } from 'humps';
import {
  toDefaultRequestTransforms,
  toDefaultResponseTransforms,
} from '@peloton/api/client-transforms';
import { ensureRequestHeaders } from '@peloton/api/ensureRequestHeaders';
import { toApiLink, toExtLinkEnv, toHref, ApiEnvs } from '@peloton/external-links/models';
import {
  toLocaleFromHostname,
  toMatchingTld,
} from '@peloton/internationalize/models/locale';
import { toNowTime } from '@peloton/time/time';
// Allowed since this is the proxy
import { BaseAPI } from '@engage/api-v2/generated/core/request';
import { removeUnusedCookies } from '@members/analytics/removeUnusedCookies';
import { toApiEnv } from '@members/env/api';

const WEB_PELOTON_PLATFORM = 'web';
const BASE_64_ENCODED_DEVICE_TYPE = btoa(
  JSON.stringify({ 'Device Type': 'Web', 'App Version': '1.0.0' }),
);

const extLink = toExtLinkEnv({
  api: toApiEnv(),
});

const addAcceptLanguageInterceptor = (client: AxiosInstance): void => {
  client.interceptors.request.use(function (config) {
    const headers = ensureRequestHeaders(config);
    const currentAcceptLangHeader = headers['Accept-Language'];
    if (!currentAcceptLangHeader || currentAcceptLangHeader === 'undefined') {
      headers['Accept-Language'] = toLocaleFromHostname();
    }
    return config;
  });
};

// Shared by the simple/singleton API clients below (pages, tooltips,
// guidance, progress-tracking, content-preferences, user-preferences):
// they all set the same platform header, and optionally the client-details
// and/or client-date headers depending on the endpoint.
type PlatformHeaderOptions = {
  includeClientDetails?: boolean;
  includeClientDate?: boolean;
};

const addPlatformHeadersInterceptor = (
  client: AxiosInstance,
  { includeClientDetails = false, includeClientDate = false }: PlatformHeaderOptions = {},
): void => {
  client.interceptors.request.use(function (config) {
    const headers = ensureRequestHeaders(config);
    headers['Peloton-Platform'] = WEB_PELOTON_PLATFORM;
    if (includeClientDetails) {
      headers['Peloton-Client-Details'] = BASE_64_ENCODED_DEVICE_TYPE;
    }
    if (includeClientDate) {
      headers['Peloton-Client-Date'] = toNowTime().format();
    }
    return config;
  });
};

const createApiClient = (responseType: ResponseType = 'json') => {
  const apiClient = axios.create({
    baseURL: toHref(toApiLink('/'), extLink),
    responseType,
    transformRequest: toDefaultRequestTransforms(),
    transformResponse: toDefaultResponseTransforms(),
  });

  addPlatformHeadersInterceptor(apiClient, { includeClientDetails: true });
  addAcceptLanguageInterceptor(apiClient);

  return apiClient;
};

const apiClient = createApiClient();
const apiClientNoAuthHeader = createApiClient();
const apiClientTextResponseType = createApiClient('text');

let isPending = false;

apiClient.interceptors.response.use(
  r => r,
  async e => {
    const originalRequest = e.config;
    if (e.response?.status === 431 && !originalRequest.retry) {
      originalRequest.retry = true;

      if (!isPending) {
        isPending = true;
        removeUnusedCookies();
      }

      try {
        return await apiClient(originalRequest);
      } finally {
        isPending = false;
      }
    }

    return Promise.reject(e);
  },
);

type PresenceEnv = 'stage' | 'test' | 'prod';

type ApiBase =
  | 'presence-leaderboard'
  | 'api-pages'
  | 'api-pages-backup'
  | 'graphql'
  | 'tooltips'
  | 'api-guidance'
  | 'progress-tracking'
  | 'api-content-preferences'
  | 'self-moderation'
  | 'user-preferences';

const toBaseURL = (apiEnv: ApiEnvs, apiBase: ApiBase) => {
  const url = (env: PresenceEnv) => `https://${apiBase}.${env}.k8s.onepeloton.com`;

  switch (apiEnv) {
    case ApiEnvs.Qa1:
      return url('stage');
    case ApiEnvs.Qa2:
      return url('test');
    case ApiEnvs.Prod:
    default:
      return url('prod');
  }
};

const toGraphQLEndpoint = () => {
  const apiEnv = toApiEnv();

  const graphqlTld = toMatchingTld(
    typeof window === 'undefined' ? '' : window.location.hostname,
  );

  const baseUrl = (env: PresenceEnv) =>
    `https://gql-graphql-gateway.${env}.k8s.onepeloton${graphqlTld}/graphql`;

  switch (apiEnv) {
    case ApiEnvs.Qa1:
      return baseUrl('stage');
    case ApiEnvs.Qa2:
      return baseUrl('test');
    case ApiEnvs.Prod:
    default:
      return baseUrl('prod');
  }
};

const presenceApiClient = axios.create({
  baseURL: toBaseURL(extLink.api, 'presence-leaderboard'),
  responseType: 'json',
  transformRequest: toDefaultRequestTransforms(),
  transformResponse: toDefaultResponseTransforms(),
});
addAcceptLanguageInterceptor(presenceApiClient);

// Shared by the simple/singleton clients below: each just varies by
// `apiBase` and which platform headers it sets.
const createSimpleApiClient = (
  apiBase: ApiBase,
  headerOptions: PlatformHeaderOptions = {},
) => {
  const client = axios.create({
    baseURL: toBaseURL(extLink.api, apiBase),
    responseType: 'json',
    transformRequest: toDefaultRequestTransforms(),
    transformResponse: toDefaultResponseTransforms(),
  });

  addPlatformHeadersInterceptor(client, headerOptions);
  addAcceptLanguageInterceptor(client);

  return client;
};

const createPagesClient = () =>
  createSimpleApiClient('api-pages', {
    includeClientDetails: true,
    includeClientDate: true,
  });

const createTooltipsClient = () =>
  createSimpleApiClient('tooltips', { includeClientDate: true });

const createGuidanceClient = () =>
  createSimpleApiClient('api-guidance', { includeClientDate: true });

const createProgressTrackingClient = () =>
  createSimpleApiClient('progress-tracking', { includeClientDate: true });

const createContentPreferencesClient = () =>
  createSimpleApiClient('api-content-preferences', { includeClientDate: true });

const createUserPreferencesClient = () =>
  createSimpleApiClient('user-preferences', { includeClientDate: true });

// Skip camelizing "__typename" key
// axios@0.33 renamed the `AxiosTransformer` type to `AxiosResponseTransformer`
// (split from the request-side `AxiosRequestTransformer`); `obj` is typed
// explicitly since axios's transform types no longer imply `any` for it.
export const toGraphQLResponseTransforms = (): AxiosResponseTransformer[] => {
  return ([] as AxiosResponseTransformer[]).concat(
    axios.defaults.transformResponse || [],
    (obj: any) =>
      camelizeKeys(obj, (key: string, convert: (key: string) => string) => {
        if (key === '__typename' || key === key.toUpperCase()) {
          return key;
        }
        return convert(key);
      }),
  );
};

const createGraphQLClient = () => {
  const graphQLApiClient = axios.create({
    baseURL: toGraphQLEndpoint(),
    transformResponse: toGraphQLResponseTransforms(),
  });

  addPlatformHeadersInterceptor(graphQLApiClient, {
    includeClientDetails: true,
    includeClientDate: true,
  });

  addAcceptLanguageInterceptor(graphQLApiClient);

  return graphQLApiClient;
};

const toMemberScoreBaseURL = (apiEnv: ApiEnvs) => {
  switch (apiEnv) {
    case ApiEnvs.Prod:
      return 'https://members-score.onepeloton.com';
    // The club-peloton service exposes a single non-prod (stage) host, which the
    // iOS app targets for both Qa1 and Qa2 (see Argon AppConfig.memberScoreHost).
    case ApiEnvs.Qa1:
    case ApiEnvs.Qa2:
    default:
      return 'https://members-score-stage.onepeloton.com';
  }
};

const createClubPelotonClient = () => {
  const clubPelotonApiClient = axios.create({
    baseURL: toMemberScoreBaseURL(extLink.api),
    responseType: 'json',
    transformRequest: toDefaultRequestTransforms(),
    transformResponse: toDefaultResponseTransforms(),
  });

  addPlatformHeadersInterceptor(clubPelotonApiClient, {
    includeClientDetails: true,
    includeClientDate: true,
  });

  addAcceptLanguageInterceptor(clubPelotonApiClient);

  return clubPelotonApiClient;
};

const pagesApiClient = createPagesClient();
const graphQLApiClient = createGraphQLClient();
const tooltipsApiClient = createTooltipsClient();
const guidanceApiClient = createGuidanceClient();
const progressTrackingApiClient = createProgressTrackingClient();
const contentPreferencesClient = createContentPreferencesClient();
const userPreferencesClient = createUserPreferencesClient();

const engageApi = new BaseAPI(apiClient);
const engageApiNoAuthHeader = new BaseAPI(apiClientNoAuthHeader);
const engageApiTextResponseType = new BaseAPI(apiClientTextResponseType);
const presenceApi = new BaseAPI(presenceApiClient);
const pagesApi = new BaseAPI(pagesApiClient);
const graphQlApi = new BaseAPI(graphQLApiClient);
const tooltipsApi = new BaseAPI(tooltipsApiClient);
const guidanceApi = new BaseAPI(guidanceApiClient);
const progressTrackingApi = new BaseAPI(progressTrackingApiClient);
const contentPreferencesApi = new BaseAPI(contentPreferencesClient);
const userPreferencesApi = new BaseAPI(userPreferencesClient);
const clubPelotonApi = new BaseAPI(createClubPelotonClient());

export {
  engageApi,
  engageApiNoAuthHeader,
  engageApiTextResponseType,
  presenceApi,
  pagesApi,
  graphQlApi,
  tooltipsApi,
  guidanceApi,
  progressTrackingApi,
  contentPreferencesApi,
  userPreferencesApi,
  clubPelotonApi,
};
